import {Component} from 'react';
import PropTypes from 'prop-types';
import './FmlxErrorBoundary.scss';
import {FmlxTextBox} from 'fmlx-common-ui';
import illustration from '../../assets/error-boundary-illustration.svg';

/**
 * FmlxErrorBoundary is a component that catch error rendering.
 *
 * @param {Object} props - Props for the FmlxErrorBoundary component.
 * @param {React.ReactNode} props.message - Message description of error boundary.
 * @param {Array} props.actions - Action that appear under message description usually button components.
 * @param {boolean} props.showStackTrace - Stack Trace error.
 * @param {React.ReactNode} props.children - It is component that will render when there is error rendering detected.
 * @returns {JSX.Element} FmlxErrorBoundary component JSX.
 */
class FmlxErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      error: null,
      errorInfo: null,
    };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo,
    });
  }

  render() {
    const {children, message, actions, showStackTrace} = this.props;

    const {errorInfo, error} = this.state;
    if (errorInfo) {
      return (
        <div className="fmlx-error-boundary-container">
          <div className="fmlx-error-boundary">
            <div className="fmlx-error-boundary__content">
              <div className="fmlx-error-boundary__content__message">
                <h2 className="title">Something Went Wrong</h2>
                <p className="description">{message}</p>
                <div className="fmlx-error-boundary__content__message__action">
                  {actions.map((action) => (
                    <div className="action-button">{action}</div>
                  ))}
                </div>
              </div>
              <div className="fmlx-error-boundary__content__illustration">
                <img
                  alt="error-boundary-illustration"
                  src={illustration}
                />
              </div>
            </div>
            {showStackTrace ? (
              <div className="fmlx-error-boundary__stack-traces">
                <FmlxTextBox rows={5} mode="textarea" value={error + errorInfo.componentStack}/>
              </div>
            ) : null}
          </div>
        </div>
      );
    }
    return children;
  }
}

FmlxErrorBoundary.propTypes = {
  message: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  actions: PropTypes.arrayOf(PropTypes.node),
  showStackTrace: PropTypes.bool,
  children: PropTypes.node,
};

FmlxErrorBoundary.defaultProps = {
  message: '',
  actions: [],
  showStackTrace: false,
  children: null,
};

export default FmlxErrorBoundary;
